/**
 * FRONT-2744 — chat previews open the Canvas instead of a new browser tab.
 *
 * Pure helpers (no DOM) deciding (a) whether a click is the plain left click
 * that may be taken over, and (b) which Canvas item, if any, an href points
 * at. Modifier clicks and middle-clicks are never taken over, so a real `href`
 * on the anchor keeps its native new-tab behaviour.
 */
import { useCanvas } from './canvas-context';
import type { CanvasItem } from './collect-items';

/** The slice of a mouse event these helpers read — a React.MouseEvent satisfies it. */
export interface ClickLike {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
  preventDefault: () => void;
}

/** Primary button, no modifier, nothing else has already handled it. */
export function isPlainLeftClick(e: Pick<ClickLike, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented'>): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;
}

function trimSlash(p: string): string {
  return p.length > 1 ? p.replace(/\/+$/, '') : p;
}

function safeDecode(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}

function parseUrl(href: string, base: string): URL | null {
  try {
    const u = new URL(href, base);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
  } catch {
    return null;
  }
}

function matchesItem(item: CanvasItem, u: URL, sameOrigin: boolean): boolean {
  if (item.kind === 'document') {
    if (!sameOrigin || !u.pathname.startsWith('/files/doc/')) return false;
    return safeDecode(u.pathname.slice('/files/doc/'.length)) === item.id;
  }
  if (item.kind === 'file') {
    return sameOrigin && trimSlash(u.pathname) === '/files' && u.searchParams.get('id') === item.id;
  }
  // sketch / deck: served on the app origin, which may differ from the SPA's.
  const itemUrl = item.url ? parseUrl(item.url, 'http://invalid.invalid') : null;
  const onItemHost = itemUrl !== null && itemUrl.host === u.host;
  if (!sameOrigin && !onItemHost) return false;
  const itemPath = item.path || `/apps/${encodeURIComponent(item.id)}/`;
  const want = parseUrl(itemPath, 'http://invalid.invalid')?.pathname ?? itemPath;
  return trimSlash(u.pathname) === trimSlash(want);
}

/**
 * The key of the Canvas item `href` points at, or null (external host, other
 * path, a sub-page of an app, `mailto:`, `#anchor`, an unparseable href).
 * Query and hash are ignored for sketches / decks; a file is identified by its
 * `?id=`.
 */
export function canvasItemKeyForHref(
  href: string | null | undefined,
  items: readonly CanvasItem[],
  currentOrigin: string,
): string | null {
  if (!href || href.startsWith('#')) return null;
  const u = parseUrl(href, currentOrigin);
  if (!u) return null;
  const sameOrigin = u.origin === currentOrigin;
  for (const item of items) {
    if (matchesItem(item, u, sameOrigin)) return item.key;
  }
  return null;
}

/**
 * An `onClick` for a card control that opens `itemKey` in the Canvas on a plain
 * left click, or undefined when there is no Canvas / it does not hold the item
 * (the control then behaves exactly as before).
 */
export function useCanvasOpenHandler(itemKey: string): ((e: ClickLike) => void) | undefined {
  const canvas = useCanvas();
  if (!canvas || !canvas.items.some((i) => i.key === itemKey)) return undefined;
  return (e) => {
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    canvas.openItem(itemKey);
  };
}
